import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ShieldCheck, Upload, Map, ClipboardCheck, RefreshCw, MessageCircle,
  GraduationCap, FileText, Sparkles, ArrowRight,
} from "lucide-react";

const steps = [
  { icon: Upload, title: "Upload", desc: "Add your brief, rubric and your own draft — PDF, DOCX or paste." },
  { icon: Map, title: "Blueprint", desc: "A section-by-section plan: purpose, criteria, word counts. No written text." },
  { icon: ClipboardCheck, title: "Evaluate", desc: "Honest rubric scores, an estimated grade range, and your strengths." },
  { icon: RefreshCw, title: "Revise", desc: "Numbered fixes anchored to your paragraphs. You make every edit." },
];

const faqs = [
  { q: "Will ThinkInk write my essay?", a: "No — never. We help you understand the brief, plan your structure and improve your own work. We don't produce text you could submit." },
  { q: "Is this allowed under academic integrity rules?", a: "ThinkInk gives feedback and planning, like a tutor would. Always check your institution's AI-use policy — we remind you to." },
  { q: "Which grading systems do you support?", a: "UK classifications (First, 2:1, 2:2) and Canadian letter grades (A+, A, B). Estimates are labelled — your marker decides the final grade." },
  { q: "What do tokens cost?", a: "You start with 20 free tokens. A blueprint is 15, a first evaluation 20, re-evaluations 15 and chat messages 2 each." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      {/* nav */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Logo />
          <div className="flex items-center gap-3">
            <Link to="/auth" data-testid="nav-login"><Button variant="ghost">Log in</Button></Link>
            <Link to="/auth?mode=register" data-testid="nav-signup">
              <Button className="bg-[#0B5D3B] text-white hover:bg-[#08472D]">Try it free</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="hero-grain">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:py-24">
          <div className="fade-up flex flex-col justify-center">
            <span className="mb-4 inline-flex w-fit items-center gap-2 rounded-full border border-emerald-800/20 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-900 dark:border-emerald-800/40 dark:bg-emerald-950 dark:text-emerald-300">
              <GraduationCap className="h-3.5 w-3.5" /> UK &amp; Canada university assignment suite
            </span>
            <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Stuck on your assignment?<br /><span className="text-[#0B5D3B] dark:text-[#25D366]">One message. Sorted.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
              Upload your brief, rubric and draft. Get a section-by-section blueprint, an honest grading evaluation, and paragraph-anchored fixes. We help you understand and improve your own work — <strong className="text-foreground">we don't write assignments.</strong>
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/auth?mode=register" data-testid="hero-cta-primary">
                <Button size="lg" className="bg-[#0B5D3B] text-white hover:bg-[#08472D]">
                  Analyse my assignment free <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href="#how" data-testid="hero-cta-secondary">
                <Button size="lg" variant="outline">See how the blueprint works</Button>
              </a>
            </div>
            <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
              <ShieldCheck className="h-4 w-4 text-[#0B5D3B] dark:text-[#25D366]" />
              Integrity-first · Your work stays yours
            </div>
          </div>

          <div className="fade-up relative flex items-center justify-center">
            <div className="w-full max-w-md rounded-3xl border border-border bg-card p-6 shadow-[0_12px_32px_rgba(11,93,59,0.12)]">
              <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                <FileText className="h-4 w-4" /> Evaluation preview
              </div>
              <div className="mb-4 rounded-2xl integrity-gradient p-4 text-white">
                <div className="text-xs uppercase tracking-wide opacity-80">Estimated grade</div>
                <div className="font-display text-3xl font-extrabold">62–68%</div>
                <div className="text-sm opacity-90">UK 2:1 · estimated — your marker decides</div>
              </div>
              {[
                { c: "Critical analysis", s: "26/40" },
                { c: "Use of authority", s: "22/30" },
                { c: "Structure & clarity", s: "15/20" },
              ].map((r) => (
                <div key={r.c} className="flex items-center justify-between border-b border-border py-2 text-sm last:border-0">
                  <span>{r.c}</span>
                  <span className="font-mono font-semibold text-[#0B5D3B] dark:text-[#25D366]">{r.s}</span>
                </div>
              ))}
              <div className="mt-4 rounded-xl bg-accent p-3 text-sm">
                <span className="font-semibold">Fix #2 · [P2]</span> — The case is mentioned but never used. Explain what the court held and tie it to your argument.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* integrity pledge */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="integrity-gradient flex flex-col items-start justify-between gap-4 rounded-3xl p-8 text-white md:flex-row md:items-center">
          <div className="flex items-start gap-4">
            <ShieldCheck className="mt-1 h-8 w-8 shrink-0" />
            <div>
              <h2 className="font-display text-2xl font-bold">We don't write your assignment. Ever.</h2>
              <p className="mt-1 max-w-2xl text-white/85">ThinkInk explains, plans and reviews — never generates paragraphs you could submit. The work stays yours, and so does the grade.</p>
            </div>
          </div>
        </div>
      </section>

      {/* how it works */}
      <section id="how" className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <h2 className="font-display text-3xl font-bold tracking-tight">How ThinkInk works</h2>
        <p className="mt-2 text-muted-foreground">Four steps from stuck to sorted.</p>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <div key={s.title} className="rounded-2xl border border-border bg-card p-6 transition-colors duration-200 hover:border-[#0B5D3B]/40">
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-[#E8F5EE] text-[#0B5D3B] dark:bg-emerald-950 dark:text-[#25D366]">
                <s.icon className="h-5 w-5" />
              </div>
              <div className="font-mono text-xs text-muted-foreground">STEP {i + 1}</div>
              <h3 className="mt-1 font-display text-lg font-semibold">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* grading showcase */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-5 md:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="font-mono text-xs uppercase tracking-wide text-muted-foreground">United Kingdom</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {["First (70+)", "2:1 (60–69)", "2:2 (50–59)", "Third (40–49)"].map((g) => (
                <span key={g} className="rounded-full bg-[#E8F5EE] px-3 py-1 text-sm font-semibold text-[#0B5D3B] dark:bg-emerald-950 dark:text-[#25D366]">{g}</span>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="font-mono text-xs uppercase tracking-wide text-muted-foreground">Canada</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {["A+ (90+)", "A (80–89)", "B (70–79)", "C (60–69)"].map((g) => (
                <span key={g} className="rounded-full bg-[#E8F5EE] px-3 py-1 text-sm font-semibold text-[#0B5D3B] dark:bg-emerald-950 dark:text-[#25D366]">{g}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* faq */}
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
        <h2 className="font-display text-3xl font-bold tracking-tight">Frequently asked</h2>
        <Accordion type="single" collapsible className="mt-6">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left font-medium">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* cta */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center rounded-3xl border border-border bg-card p-10 text-center">
          <Sparkles className="h-8 w-8 text-[#0B5D3B] dark:text-[#25D366]" />
          <h2 className="mt-4 font-display text-3xl font-bold">Start with 20 free tokens</h2>
          <p className="mt-2 max-w-md text-muted-foreground">Enough for one full evaluation, or a blueprint plus a chat. No card needed.</p>
          <Link to="/auth?mode=register" className="mt-6" data-testid="cta-signup">
            <Button size="lg" className="bg-[#0B5D3B] text-white hover:bg-[#08472D]">Create my free account</Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-3 px-4 text-sm text-muted-foreground sm:px-6 lg:px-8">
          <Logo />
          <p>We help you understand and improve your own work. We don't write assignments.</p>
          <p className="text-xs">© {new Date().getFullYear()} ThinkInk</p>
        </div>
      </footer>
    </div>
  );
}
